/**
 * SettingsModal - Modal für Benutzereinstellungen
 * Ermöglicht Passwort anzeigen, Organisationsname ändern und Profilbild hochladen
 */

import React, { useState, useEffect, useRef } from 'react';
import DeleteConfirmModal from './DeleteConfirmModal.jsx';

const EyeIcon = ({ off }) => off ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>
);

const SLabel = ({ children }) => (
    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', marginBottom: 6 }}>{children}</label>
);

const SInput = (props) => (
    <input {...props} className="stm-input" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 500, color: '#1e293b', background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 150ms ease, box-shadow 150ms ease', ...props.style }} />
);

const SBtn = ({ children, variant = 'secondary', ...rest }) => {
    const styles = {
        primary: { height: 36, padding: '0 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: rest.disabled ? 'not-allowed' : 'pointer', boxShadow: rest.disabled ? 'none' : '0 2px 8px rgba(79,70,229,.25)', opacity: rest.disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 120ms ease' },
        secondary: { height: 36, padding: '0 14px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: rest.disabled ? 'not-allowed' : 'pointer', opacity: rest.disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 120ms ease' },
        danger: { height: 32, padding: '0 12px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: rest.disabled ? 'not-allowed' : 'pointer', opacity: rest.disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'all 120ms ease' },
        ghost: { height: 32, padding: '0 10px', borderRadius: 8, border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 12, fontWeight: 600, cursor: rest.disabled ? 'not-allowed' : 'pointer', opacity: rest.disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'all 120ms ease' },
        sm: { height: 30, padding: '0 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: rest.disabled ? 'not-allowed' : 'pointer', opacity: rest.disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'all 120ms ease' },
    };
    return <button type="button" {...rest} style={{ ...styles[variant], ...rest.style }}>{children}</button>;
};

const RoleBadge = ({ role }) => {
    const m = { owner: { bg: '#faf5ff', fg: '#6d28d9', border: '#ddd6fe' }, admin: { bg: '#eef2ff', fg: '#4338ca', border: '#c7d2fe' }, editor: { bg: '#ecfdf3', fg: '#065f46', border: '#a7f3d0' }, member: { bg: '#f8fafc', fg: '#475569', border: '#e2e8f0' }, viewer: { bg: '#fffbeb', fg: '#92400e', border: '#fde68a' } };
    const s = m[role] || m.member;
    return <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.fg, border: `1px solid ${s.border}`, whiteSpace: 'nowrap' }}>{role}</span>;
};

const StatusDot = ({ status }) => {
    const c = { pending: '#f59e0b', accepted: '#10b981', revoked: '#ef4444', expired: '#94a3b8' };
    return <span style={{ width: 6, height: 6, borderRadius: '50%', background: c[status] || c.pending, flexShrink: 0 }} />;
};

const SettingsModal = ({ isOpen, onClose, supabase, organizationService, authManager, showNotification, onProfileImageUploaded }) => {
    const t = (key, fallback, params = {}) => {
        if (window.i18n && typeof window.i18n.t === 'function') {
            const result = window.i18n.t(key, params);
            if (typeof result === 'string' && result !== key) return result;
        }
        return fallback || key;
    };
    const [organizationName, setOrganizationName] = useState('');
    const [isLoadingOrg, setIsLoadingOrg] = useState(false);
    const [isSavingOrg, setIsSavingOrg] = useState(false);
    // Phase 1: Kontenrahmen pro Org (SKR04 / SKR03 / custom)
    const [chartOfAccounts, setChartOfAccounts] = useState('SKR04');
    const [isSavingChartOfAccounts, setIsSavingChartOfAccounts] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const fileInputRef = useRef(null);
    const [user, setUser] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSavingName, setIsSavingName] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [language, setLanguage] = useState('de'); // 'de' oder 'en'
    const [currentOrgRole, setCurrentOrgRole] = useState(null);
    const [invites, setInvites] = useState([]);
    const [isLoadingInvites, setIsLoadingInvites] = useState(false);
    const [isCreatingInvite, setIsCreatingInvite] = useState(false);
    const [revokingInviteId, setRevokingInviteId] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('viewer');
    const [inviteExpiresInHours, setInviteExpiresInHours] = useState(72);
    const [lastInviteToken, setLastInviteToken] = useState('');
    const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
    const [inviteLinkCopiedId, setInviteLinkCopiedId] = useState(null);
    const [storedInviteLinks, setStoredInviteLinks] = useState({});
    const [members, setMembers] = useState([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [updatingMemberRoleUserId, setUpdatingMemberRoleUserId] = useState(null);
    const [removingMemberUserId, setRemovingMemberUserId] = useState(null);
    const [transferringOwnerUserId, setTransferringOwnerUserId] = useState(null);
    const [deletingInviteId, setDeletingInviteId] = useState(null);
    const [pendingInviteDelete, setPendingInviteDelete] = useState(null);
    const [invitesSectionExpanded, setInvitesSectionExpanded] = useState(() => {
        try {
            return localStorage.getItem('settingsInvitesSectionExpanded') === 'true';
        } catch (_) {
            return false;
        }
    });
    const [settingsActiveSection, setSettingsActiveSection] = useState('personal'); // 'personal' | 'workspace'
    // Organisations-Logo (Sidebar)
    const [organizationLogoUrl, setOrganizationLogoUrl] = useState(null);
    const [pendingLogoFile, setPendingLogoFile] = useState(null);
    const [pendingLogoPreviewUrl, setPendingLogoPreviewUrl] = useState(null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const logoFileInputRef = useRef(null);

    const canManageInvites = currentOrgRole === 'admin' || currentOrgRole === 'owner';

    const toggleInvitesSection = () => {
        const next = !invitesSectionExpanded;
        setInvitesSectionExpanded(next);
        try {
            localStorage.setItem('settingsInvitesSectionExpanded', next ? 'true' : 'false');
        } catch (_) {}
    };
    const canManageMembers = currentOrgRole === 'admin' || currentOrgRole === 'owner';

    const INVITE_LINKS_STORAGE_KEY = 'settingsInviteLinks';

    const getInviteLinkFromToken = (token) => {
        if (!token || typeof window === 'undefined') return '';
        return `${window.location.origin}/index.html#login?invite_token=${encodeURIComponent(token)}`;
    };

    const getStoredInviteLinks = (orgId) => {
        if (!orgId) return {};
        try {
            const raw = localStorage.getItem(INVITE_LINKS_STORAGE_KEY);
            const all = raw ? JSON.parse(raw) : {};
            return all[orgId] || {};
        } catch (_) {
            return {};
        }
    };

    const setStoredInviteLink = (orgId, inviteId, token) => {
        if (!orgId || !inviteId || !token) return;
        try {
            const raw = localStorage.getItem(INVITE_LINKS_STORAGE_KEY);
            const all = raw ? JSON.parse(raw) : {};
            if (!all[orgId]) all[orgId] = {};
            all[orgId][inviteId] = token;
            localStorage.setItem(INVITE_LINKS_STORAGE_KEY, JSON.stringify(all));
        } catch (_) {}
    };

    const removeStoredInviteLink = (orgId, inviteId) => {
        if (!orgId || !inviteId) return;
        try {
            const raw = localStorage.getItem(INVITE_LINKS_STORAGE_KEY);
            const all = raw ? JSON.parse(raw) : {};
            if (all[orgId]) {
                delete all[orgId][inviteId];
                localStorage.setItem(INVITE_LINKS_STORAGE_KEY, JSON.stringify(all));
            }
        } catch (_) {}
    };

    const getInviteStatusLabel = (invite) => {
        if (!invite) return t('settings.invites.status.unknown', 'unbekannt');
        const status = String(invite.status || '').toLowerCase();
        if (status === 'accepted') return t('settings.invites.status.accepted', 'angenommen');
        if (status === 'revoked') return t('settings.invites.status.revoked', 'zurückgezogen');
        if (status === 'expired') return t('settings.invites.status.expired', 'abgelaufen');
        return t('settings.invites.status.pending', 'ausstehend');
    };

    const formatDate = (value) => {
        if (!value) return '-';
        try {
            const locale = language === 'en' ? 'en-US' : 'de-DE';
            return new Date(value).toLocaleString(locale);
        } catch (_) {
            return String(value);
        }
    };

    // Lade Daten beim Öffnen
    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen]);

    const loadData = async () => {
        try {
            // Lade Sprache aus localStorage
            const savedLanguage = localStorage.getItem('userLanguage') || 'de';
            setLanguage(savedLanguage);

            // Lade aktuellen Benutzer
            if (authManager && authManager.currentUser) {
                const currentUser = authManager.currentUser;
                setUser(currentUser);
                
                // Lade Profilbild falls vorhanden
                await loadProfileImage(currentUser.id);
            }

            // Lade Organisationsname
            if (organizationService) {
                const org = organizationService.getCurrentOrganization();
                if (org) {
                    setOrganizationName(org.name);
                    setCurrentOrgRole(org.role || null);
                    setOrganizationLogoUrl(org.logoUrl || null);
                    setPendingLogoFile(null);
                    setPendingLogoPreviewUrl(null);
                }
            }

            // Phase 1: Lade Kontenrahmen der Org (chart_of_accounts)
            if (supabase && organizationService) {
                try {
                    const coaOrgId = organizationService.getCurrentOrganizationId();
                    if (coaOrgId) {
                        const { data: coaRow } = await supabase
                            .from('new_organizations')
                            .select('chart_of_accounts')
                            .eq('id', coaOrgId)
                            .maybeSingle();
                        if (coaRow?.chart_of_accounts) {
                            setChartOfAccounts(coaRow.chart_of_accounts);
                        }
                    }
                } catch (e) {
                    console.warn('SettingsModal: chart_of_accounts konnte nicht geladen werden', e);
                }
            }

            // Lade Vor- und Nachname aus new_user_organizations (organisationsspezifisch)
            if (supabase && authManager && authManager.currentUser && organizationService) {
                const orgId = organizationService.getCurrentOrganizationId();
                if (orgId) {
                    const { data, error } = await supabase
                        .from('new_user_organizations')
                        .select('first_name, last_name')
                        .eq('user_id', authManager.currentUser.id)
                        .eq('organization_id', orgId)
                        .single();

                    if (!error && data) {
                        // Verwende Namen aus new_user_organizations falls vorhanden
                        setFirstName(data.first_name || '');
                        setLastName(data.last_name || '');
                    } else {
                        // Fallback: Lade aus user_metadata
                        const metadata = authManager.currentUser.user_metadata || {};
                        setFirstName(metadata.first_name || '');
                        setLastName(metadata.last_name || '');
                    }
                } else {
                    // Fallback: Lade aus user_metadata
                    const metadata = authManager.currentUser.user_metadata || {};
                    setFirstName(metadata.first_name || '');
                    setLastName(metadata.last_name || '');
                }
            }

            const orgId = organizationService && organizationService.getCurrentOrganizationId
                ? organizationService.getCurrentOrganizationId()
                : null;
            const role = organizationService && organizationService.getCurrentOrganization
                ? organizationService.getCurrentOrganization()?.role
                : null;
            if (orgId && (role === 'admin' || role === 'owner')) {
                await Promise.all([
                    loadInvites(orgId),
                    loadMembers(orgId)
                ]);
            } else {
                setInvites([]);
                setMembers([]);
            }
        } catch (error) {
            console.error('Fehler beim Laden der Daten:', error);
            if (showNotification) {
                showNotification(t('settings.errorLoading', 'Fehler beim Laden der Einstellungen'), 'error');
            }
        }
    };

    // Sidebar-Logo: Datei wählen, hochladen, entfernen
    const handleLogoFileSelect = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        if (!file.type || !file.type.startsWith('image/')) {
            if (showNotification) showNotification(t('settings.logoInvalidType', 'Bitte eine Bilddatei auswählen (PNG, JPG, SVG).'), 'error');
            return;
        }
        const maxBytes = 2 * 1024 * 1024;
        if (file.size > maxBytes) {
            if (showNotification) showNotification(t('settings.logoTooLarge', 'Das Logo ist zu groß. Maximal 2 MB erlaubt.'), 'error');
            return;
        }
        setPendingLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setPendingLogoPreviewUrl(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleLogoUpload = async () => {
        if (!pendingLogoFile || !supabase || !organizationService) return;
        const orgId = organizationService.getCurrentOrganizationId();
        if (!orgId) {
            if (showNotification) showNotification(t('settings.logoNoOrg', 'Keine Organisation aktiv.'), 'error');
            return;
        }
        setIsUploadingLogo(true);
        try {
            const fileExt = (pendingLogoFile.name.split('.').pop() || 'png').toLowerCase();
            const fileName = `${orgId}/logo.${fileExt}`;

            // Alte Logos in diesem Org-Ordner aufräumen
            try {
                const { data: oldFiles } = await supabase.storage.from('org-logos').list(orgId);
                if (oldFiles && oldFiles.length > 0) {
                    const filesToDelete = oldFiles.map(f => `${orgId}/${f.name}`);
                    await supabase.storage.from('org-logos').remove(filesToDelete);
                }
            } catch (_) {
                // ignorieren — Aufräumen ist Best-Effort
            }

            const { error: uploadError } = await supabase.storage
                .from('org-logos')
                .upload(fileName, pendingLogoFile, { cacheControl: '3600', upsert: true });
            if (uploadError) {
                if (uploadError.message && uploadError.message.toLowerCase().includes('not found')) {
                    throw new Error(t('settings.logoBucketMissing', 'Storage-Bucket "org-logos" existiert nicht. Bitte in Supabase anlegen.'));
                }
                throw uploadError;
            }

            const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(fileName);
            const publicUrl = urlData && urlData.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null;
            if (!publicUrl) throw new Error('Public URL konnte nicht ermittelt werden');

            await organizationService.updateOrganizationLogo(orgId, publicUrl);
            setOrganizationLogoUrl(publicUrl);
            setPendingLogoFile(null);
            setPendingLogoPreviewUrl(null);
            if (logoFileInputRef.current) logoFileInputRef.current.value = '';
            if (showNotification) showNotification(t('settings.logoUploaded', 'Logo erfolgreich hochgeladen.'), 'success');
        } catch (error) {
            console.error('Fehler beim Hochladen des Logos:', error);
            if (showNotification) showNotification(error.message || t('settings.logoUploadError', 'Fehler beim Hochladen des Logos.'), 'error');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleLogoRemove = async () => {
        if (!supabase || !organizationService) return;
        const orgId = organizationService.getCurrentOrganizationId();
        if (!orgId) return;
        setIsUploadingLogo(true);
        try {
            // Dateien im Bucket aufräumen (Best-Effort)
            try {
                const { data: oldFiles } = await supabase.storage.from('org-logos').list(orgId);
                if (oldFiles && oldFiles.length > 0) {
                    const filesToDelete = oldFiles.map(f => `${orgId}/${f.name}`);
                    await supabase.storage.from('org-logos').remove(filesToDelete);
                }
            } catch (_) {
                // ignorieren
            }
            await organizationService.updateOrganizationLogo(orgId, null);
            setOrganizationLogoUrl(null);
            setPendingLogoFile(null);
            setPendingLogoPreviewUrl(null);
            if (logoFileInputRef.current) logoFileInputRef.current.value = '';
            if (showNotification) showNotification(t('settings.logoRemoved', 'Logo entfernt.'), 'success');
        } catch (error) {
            console.error('Fehler beim Entfernen des Logos:', error);
            if (showNotification) showNotification(t('settings.logoRemoveError', 'Logo konnte nicht entfernt werden.'), 'error');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    // Phase 1: Persistiert den Kontenrahmen pro Org. Beim Wechsel sofort speichern.
    const handleChartOfAccountsChange = async (nextValue) => {
        if (nextValue === chartOfAccounts) return;
        if (!supabase || !organizationService) return;
        const orgId = organizationService.getCurrentOrganizationId();
        if (!orgId) {
            if (showNotification) showNotification(t('settings.accounting.errorNoOrg', 'Keine Organisation aktiv'), 'error');
            return;
        }
        const previous = chartOfAccounts;
        setChartOfAccounts(nextValue);
        setIsSavingChartOfAccounts(true);
        try {
            const { error } = await supabase
                .from('new_organizations')
                .update({ chart_of_accounts: nextValue })
                .eq('id', orgId);
            if (error) throw error;
            if (showNotification) {
                showNotification(t('settings.accounting.saved', 'Kontenrahmen gespeichert: {value}', { value: nextValue }), 'success');
            }
        } catch (error) {
            console.error('SettingsModal: chart_of_accounts speichern fehlgeschlagen', error);
            setChartOfAccounts(previous);
            if (showNotification) {
                showNotification(t('settings.accounting.errorSave', 'Kontenrahmen konnte nicht gespeichert werden'), 'error');
            }
        } finally {
            setIsSavingChartOfAccounts(false);
        }
    };

    const loadInvites = async (orgId = null) => {
        if (!organizationService || !organizationService.listOrganizationInvites) {
            setInvites([]);
            return;
        }

        setIsLoadingInvites(true);
        try {
            const result = await organizationService.listOrganizationInvites(orgId || undefined);
            setInvites(Array.isArray(result) ? result : []);
            const currentOrgId = orgId || organizationService.getCurrentOrganizationId();
            if (currentOrgId) {
                setStoredInviteLinks(getStoredInviteLinks(currentOrgId));
            }
        } catch (error) {
            setInvites([]);
            if (showNotification) {
                showNotification(error.message || t('settings.invites.errorLoading', 'Fehler beim Laden der Einladungen'), 'error');
            }
        } finally {
            setIsLoadingInvites(false);
        }
    };

    const loadMembers = async (orgId = null) => {
        if (!organizationService || !organizationService.listOrganizationMembers) {
            setMembers([]);
            return;
        }

        setIsLoadingMembers(true);
        try {
            const result = await organizationService.listOrganizationMembers(orgId || undefined);
            setMembers(Array.isArray(result) ? result : []);
        } catch (error) {
            setMembers([]);
            if (showNotification) {
                showNotification(error.message || t('settings.members.errorLoading', 'Fehler beim Laden der Team-Mitglieder'), 'error');
            }
        } finally {
            setIsLoadingMembers(false);
        }
    };

    const loadProfileImage = async (userId) => {
        try {
            if (!supabase) return;

            // Prüfe ob Profilbild existiert
            const { data, error } = await supabase
                .storage
                .from('profile-images')
                .list(userId, {
                    limit: 1,
                    sortBy: { column: 'created_at', order: 'desc' }
                });

            if (error) {
                // Wenn Bucket nicht existiert, ignoriere den Fehler
                if (error.message && error.message.includes('not found')) {
                    return;
                }
                console.error('Fehler beim Laden des Profilbilds:', error);
                return;
            }

            if (data && data.length > 0) {
                const { data: urlData } = supabase
                    .storage
                    .from('profile-images')
                    .getPublicUrl(`${userId}/${data[0].name}`);
                
                if (urlData) {
                    setProfileImageUrl(urlData.publicUrl);
                }
            }
        } catch (error) {
            console.error('Fehler beim Laden des Profilbilds:', error);
        }
    };

    const handlePasswordToggle = () => {
        setShowPasswords(prev => !prev);
    };

    const handleCreateInvite = async () => {
        if (!organizationService || !canManageInvites) return;

        const email = String(inviteEmail || '').trim().toLowerCase();
        if (!email) {
            if (showNotification) {
                showNotification(t('settings.invites.emailRequired', 'Bitte E-Mail eingeben'), 'error');
            }
            return;
        }

        setIsCreatingInvite(true);
        setInviteLinkCopied(false);
        try {
            const response = await organizationService.createOrganizationInvite({
                email,
                role: inviteRole,
                expiresInHours: Number(inviteExpiresInHours) || 72
            });

            setInviteEmail('');
            const inviteToken = response && (response.invite_token || response.inviteToken);
            const inviteId = response && (response.invite_id || response.inviteId);
            setLastInviteToken(inviteToken || '');
            const orgId = organizationService.getCurrentOrganizationId();
            if (orgId && inviteId && inviteToken) {
                setStoredInviteLink(orgId, inviteId, inviteToken);
                setStoredInviteLinks((prev) => ({ ...prev, [inviteId]: inviteToken }));
            }
            await loadInvites();

            if (showNotification) {
                showNotification(t('settings.invites.created', 'Einladung erstellt'), 'success');
            }
        } catch (error) {
            if (showNotification) {
                showNotification(error.message || t('settings.invites.errorCreating', 'Fehler beim Erstellen der Einladung'), 'error');
            }
        } finally {
            setIsCreatingInvite(false);
        }
    };

    const handleCopyInviteLink = async () => {
        const link = getInviteLinkFromToken(lastInviteToken);
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setInviteLinkCopied(true);
            if (showNotification) {
                showNotification(t('settings.invites.linkCopied', 'Einladungslink kopiert'), 'success');
            }
        } catch (_) {
            if (showNotification) {
                showNotification(t('settings.invites.copyFailed', 'Kopieren fehlgeschlagen'), 'error');
            }
        }
    };

    const handleRevokeInvite = async (inviteId) => {
        if (!inviteId || !organizationService || !canManageInvites) return;
        setRevokingInviteId(inviteId);
        try {
            await organizationService.revokeOrganizationInvite(inviteId);
            const orgId = organizationService.getCurrentOrganizationId();
            if (orgId) removeStoredInviteLink(orgId, inviteId);
            await loadInvites();
            if (orgId) setStoredInviteLinks(getStoredInviteLinks(orgId));
            if (showNotification) {
                showNotification(t('settings.invites.revoked', 'Einladung zurückgezogen'), 'success');
            }
        } catch (error) {
            if (showNotification) {
                showNotification(error.message || t('settings.invites.errorRevoking', 'Fehler beim Zurückziehen der Einladung'), 'error');
            }
        } finally {
            setRevokingInviteId(null);
        }
    };

    const handleDeleteInvite = async (inviteId) => {
        if (!inviteId || !organizationService?.deleteOrganizationInvite || !canManageInvites) return;
        setDeletingInviteId(inviteId);
        try {
            await new Promise((resolve) => setTimeout(resolve, 220));
            await organizationService.deleteOrganizationInvite(inviteId);
            const orgId = organizationService.getCurrentOrganizationId();
            if (orgId) removeStoredInviteLink(orgId, inviteId);
            await loadInvites();
            if (orgId) setStoredInviteLinks(getStoredInviteLinks(orgId));
            if (showNotification) {
                showNotification(t('settings.invites.deleted', 'Einladung gelöscht'), 'success');
            }
        } catch (error) {
            if (showNotification) {
                showNotification(error.message || t('settings.invites.errorDeleting', 'Fehler beim Löschen der Einladung'), 'error');
            }
        } finally {
            setDeletingInviteId(null);
        }
    };

    const handleRequestInviteDelete = (invite) => {
        if (!invite?.id || deletingInviteId === invite.id || !canManageInvites) return;
        setPendingInviteDelete({
            id: invite.id,
            email: invite.email || ''
        });
    };

    const handleCloseInviteDeleteModal = () => {
        if (deletingInviteId) return;
        setPendingInviteDelete(null);
    };

    const handleConfirmInviteDelete = async () => {
        const inviteId = pendingInviteDelete?.id;
        if (!inviteId) return;
        setPendingInviteDelete(null);
        await handleDeleteInvite(inviteId);
    };

    const handleCopyInviteLinkForId = async (inviteId) => {
        const token = storedInviteLinks[inviteId];
        const link = getInviteLinkFromToken(token);
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setInviteLinkCopiedId(inviteId);
            if (showNotification) {
                showNotification(t('settings.invites.linkCopied', 'Einladungslink kopiert'), 'success');
            }
            setTimeout(() => setInviteLinkCopiedId(null), 2000);
        } catch (_) {
            if (showNotification) {
                showNotification(t('settings.invites.copyFailed', 'Kopieren fehlgeschlagen'), 'error');
            }
        }
    };

    const handleMemberRoleChange = async (member, nextRole) => {
        if (!member || !member.user_id || !organizationService || !canManageMembers) {
            return;
        }

        const currentRole = String(member.role || '').toLowerCase();
        const normalizedNextRole = String(nextRole || '').toLowerCase();
        if (!normalizedNextRole || normalizedNextRole === currentRole) {
            return;
        }

        setUpdatingMemberRoleUserId(member.user_id);
        try {
            await organizationService.updateOrganizationMemberRole({
                userId: member.user_id,
                role: normalizedNextRole
            });
            await loadMembers();

            // Eigene Rolle im Modal sofort aktualisieren.
            if (member.is_self) {
                const role = organizationService && organizationService.getCurrentOrganization
                    ? organizationService.getCurrentOrganization()?.role
                    : null;
                setCurrentOrgRole(role || null);
            }

            if (showNotification) {
                showNotification(t('settings.members.roleUpdated', 'Rolle erfolgreich aktualisiert'), 'success');
            }
        } catch (error) {
            if (showNotification) {
                showNotification(error.message || t('settings.members.errorUpdatingRole', 'Fehler beim Aktualisieren der Rolle'), 'error');
            }
        } finally {
            setUpdatingMemberRoleUserId(null);
        }
    };

    const handleRemoveMember = async (member) => {
        if (!member || !member.user_id || !organizationService || !canManageMembers) {
            return;
        }

        const memberRole = String(member.role || '').toLowerCase();
        if (memberRole === 'owner') {
            if (showNotification) {
                showNotification(t('settings.members.ownerCannotBeRemoved', 'Owner kann hier nicht entfernt werden'), 'error');
            }
            return;
        }

        const identity = member.email || member.user_id;
        const confirmText = t('settings.members.removeConfirm', 'Mitglied "{identity}" wirklich entfernen?\n\nDer Account wird dabei vollständig gelöscht (kein Login mehr möglich).', { identity });
        if (!window.confirm(confirmText)) {
            return;
        }

        setRemovingMemberUserId(member.user_id);
        try {
            const result = await organizationService.removeOrganizationMember({
                userId: member.user_id
            });
            await loadMembers();
            if (showNotification) {
                if (result && result.account_deleted) {
                    showNotification(t('settings.members.removedAndDeleted', 'Mitglied entfernt und Account gelöscht'), 'success');
                } else {
                    showNotification(t('settings.members.removed', 'Mitglied entfernt'), 'success');
                }
            }
        } catch (error) {
            if (showNotification) {
                showNotification(error.message || t('settings.members.errorRemoving', 'Fehler beim Entfernen des Mitglieds'), 'error');
            }
        } finally {
            setRemovingMemberUserId(null);
        }
    };

    const handleTransferOwner = async (member) => {
        if (!member || !member.user_id || !organizationService) {
            return;
        }

        if (currentOrgRole !== 'owner') {
            if (showNotification) {
                showNotification(t('settings.members.onlyOwnerCanTransfer', 'Nur Owner kann die Inhaberschaft übertragen'), 'error');
            }
            return;
        }

        if (member.is_self || String(member.role || '').toLowerCase() === 'owner') {
            return;
        }

        const identity = member.email || member.user_id;
        const confirmText = t('settings.members.transferConfirm', 'Inhaberschaft wirklich an "{identity}" übertragen?\n\nDeine Rolle wird danach auf "admin" gesetzt.', { identity });
        if (!window.confirm(confirmText)) {
            return;
        }

        setTransferringOwnerUserId(member.user_id);
        try {
            await organizationService.transferOrganizationOwner({
                userId: member.user_id
            });
            await loadMembers();

            const role = organizationService && organizationService.getCurrentOrganization
                ? organizationService.getCurrentOrganization()?.role
                : null;
            setCurrentOrgRole(role || null);

            if (showNotification) {
                showNotification(t('settings.members.ownershipTransferred', 'Inhaberschaft erfolgreich übertragen'), 'success');
            }
        } catch (error) {
            if (showNotification) {
                showNotification(error.message || t('settings.members.errorTransferringOwnership', 'Fehler beim Übertragen der Inhaberschaft'), 'error');
            }
        } finally {
            setTransferringOwnerUserId(null);
        }
    };

    const handleOrganizationNameChange = async () => {
        if (!organizationName.trim()) {
            if (showNotification) {
                showNotification(t('settings.organizationNameRequired', 'Bitte geben Sie einen Organisationsnamen ein'), 'error');
            }
            return;
        }

        setIsSavingOrg(true);
        try {
            const org = organizationService.getCurrentOrganization();
            if (!org) {
                throw new Error(t('settings.noOrganizationSelected', 'Keine Organisation ausgewählt'));
            }

            await organizationService.renameOrganization(org.id, organizationName.trim());
            
            if (showNotification) {
                showNotification(t('settings.organizationNameUpdated', 'Organisationsname erfolgreich geändert'), 'success');
            }
        } catch (error) {
            console.error('Fehler beim Ändern des Organisationsnamens:', error);
            if (showNotification) {
                showNotification(error.message || t('settings.organizationNameUpdateError', 'Fehler beim Ändern des Organisationsnamens'), 'error');
            }
        } finally {
            setIsSavingOrg(false);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validierung
        if (!file.type.startsWith('image/')) {
            if (showNotification) {
                showNotification(t('settings.imageSelectError', 'Bitte wählen Sie eine Bilddatei aus'), 'error');
            }
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB Limit
            if (showNotification) {
                showNotification(t('settings.imageTooLarge', 'Das Bild ist zu groß. Maximal 5MB erlaubt.'), 'error');
            }
            return;
        }

        setProfileImage(file);
        
        // Vorschau
        const reader = new FileReader();
        reader.onload = (e) => {
            setProfileImageUrl(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleImageUpload = async (silent = false) => {
        if (!profileImage || !user || !supabase) {
            return false;
        }

        setIsUploadingImage(true);
        try {
            const fileExt = profileImage.name.split('.').pop();
            const fileName = `${user.id}/avatar.${fileExt}`;

            // Lösche altes Bild falls vorhanden
            try {
                const { data: oldFiles } = await supabase
                    .storage
                    .from('profile-images')
                    .list(user.id);
                
                if (oldFiles && oldFiles.length > 0) {
                    const filesToDelete = oldFiles.map(f => `${user.id}/${f.name}`);
                    await supabase
                        .storage
                        .from('profile-images')
                        .remove(filesToDelete);
                }
            } catch (error) {
                // Ignoriere Fehler beim Löschen
            }

            // Upload neues Bild
            const { error: uploadError } = await supabase
                .storage
                .from('profile-images')
                .upload(fileName, profileImage, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (uploadError) {
                if (uploadError.message && uploadError.message.includes('not found')) {
                    throw new Error(t('settings.imageBucketMissing', 'Storage-Bucket "profile-images" existiert nicht. Bitte erstellen Sie das Bucket in Supabase.'));
                }
                throw uploadError;
            }

            // Hole öffentliche URL
            const { data: urlData } = supabase
                .storage
                .from('profile-images')
                .getPublicUrl(fileName);

            if (urlData) {
                setProfileImageUrl(urlData.publicUrl);
            }

            if (!silent && showNotification) {
                showNotification(t('settings.imageUploaded', 'Profilbild erfolgreich hochgeladen'), 'success');
            }

            // Benachrichtige Parent-Komponente über erfolgreichen Upload
            if (onProfileImageUploaded) {
                onProfileImageUploaded();
            }

            // Reset file input
            setProfileImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            return true;
        } catch (error) {
            console.error('Fehler beim Hochladen des Profilbilds:', error);
            if (!silent && showNotification) {
                showNotification(t('settings.imageUploadError', 'Fehler beim Hochladen des Profilbilds'), 'error');
            }
            return false;
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleChangePassword = async () => {
        if (!supabase || !user) {
            if (showNotification) {
                showNotification(t('settings.userDataUnavailable', 'Benutzerdaten nicht verfügbar'), 'error');
            }
            return false;
        }

        // Validierung
        if (!newPassword.trim()) {
            if (showNotification) {
                showNotification(t('settings.newPasswordRequired', 'Bitte geben Sie ein neues Passwort ein'), 'error');
            }
            return false;
        }

        if (newPassword.length < 6) {
            if (showNotification) {
                showNotification(t('settings.passwordMinLength', 'Das Passwort muss mindestens 6 Zeichen lang sein'), 'error');
            }
            return false;
        }

        if (newPassword !== confirmPassword) {
            if (showNotification) {
                showNotification(t('settings.passwordMismatch', 'Die Passwörter stimmen nicht überein'), 'error');
            }
            return false;
        }

        setIsChangingPassword(true);
        try {
            // Ändere das Passwort
            // Supabase erlaubt Passwort-Änderung für eingeloggte Benutzer
            const { error: updateError } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (updateError) {
                throw updateError;
            }

            // Reset Passwort-Felder
            setNewPassword('');
            setConfirmPassword('');

            if (showNotification) {
                showNotification(t('settings.passwordUpdated', 'Passwort erfolgreich geändert'), 'success');
            }

            return true;
        } catch (error) {
            console.error('Fehler beim Ändern des Passworts:', error);
            if (showNotification) {
                const errorMessage = error.message || 'Fehler beim Ändern des Passworts';
                showNotification(errorMessage, 'error');
            }
            return false;
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleSaveName = async () => {
        if (!supabase || !user || !organizationService) {
            if (showNotification) {
                showNotification(t('settings.userDataUnavailable', 'Benutzerdaten nicht verfügbar'), 'error');
            }
            return false;
        }

        try {
            const orgId = organizationService.getCurrentOrganizationId();
            if (!orgId) {
                throw new Error(t('settings.noOrganizationSelected', 'Keine Organisation ausgewählt'));
            }

            // 1. Aktualisiere user_metadata (für globale Anzeige)
            const { error: authError } = await supabase.auth.updateUser({
                data: {
                    first_name: firstName.trim(),
                    last_name: lastName.trim()
                }
            });

            if (authError) throw authError;

            // 2. Aktualisiere new_user_organizations (organisationsspezifisch)
            const { error: dbError } = await supabase
                .from('new_user_organizations')
                .update({
                    first_name: firstName.trim() || null,
                    last_name: lastName.trim() || null,
                    updated_at: new Date().toISOString()
                })
                .eq('user_id', user.id)
                .eq('organization_id', orgId);

            if (dbError) {
                console.warn('Fehler beim Aktualisieren in new_user_organizations:', dbError);
                // Nicht kritisch - user_metadata wurde bereits aktualisiert
            }

            // Aktualisiere lokalen User-State
            const updatedUser = { ...user };
            updatedUser.user_metadata = {
                ...updatedUser.user_metadata,
                first_name: firstName.trim(),
                last_name: lastName.trim()
            };
            setUser(updatedUser);

            // Aktualisiere auch im AuthManager
            if (authManager) {
                authManager.currentUser = updatedUser;
                authManager.updateUI();
            }

            // Aktualisiere Namen in der Sidebar
            if (window.app && typeof window.app.loadUserName === 'function') {
                window.app.loadUserName();
            }

            return true;
        } catch (error) {
            console.error('Fehler beim Speichern des Namens:', error);
            if (showNotification) {
                showNotification(error.message || t('settings.nameSaveError', 'Fehler beim Speichern des Namens'), 'error');
            }
            return false;
        }
    };

    const handleLanguageChange = async (newLanguage) => {
        setLanguage(newLanguage);
        localStorage.setItem('userLanguage', newLanguage);
        
        // Ändere Sprache im i18n-System
        if (window.i18n) {
            await window.i18n.setLanguage(newLanguage);
        }
        
        // Optional: Sofortige Benachrichtigung
        if (showNotification) {
            const message = window.i18n ? window.i18n.t('settings.languageChanged') : 'Sprache geändert';
            showNotification(message, 'success');
        }
        
        // Lade Seite neu, damit alle Texte aktualisiert werden
        // Alternativ: Dispatch Event und alle Komponenten reagieren darauf
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: newLanguage } }));
    };

    const handleSaveAll = async () => {
        setIsSavingName(true);
        let hasChanges = false;
        let allSuccess = true;

        try {
            // 1. Speichere Profilbild falls ausgewählt
            if (profileImage) {
                hasChanges = true;
                const imageSuccess = await handleImageUpload(true); // silent mode
                if (!imageSuccess) {
                    allSuccess = false;
                }
            }

            // 2. Speichere Vor- und Nachname (immer versuchen, auch wenn leer)
            hasChanges = true;
            const nameSuccess = await handleSaveName();
            if (!nameSuccess) {
                allSuccess = false;
            }

            // 3. Ändere Passwort nur wenn BEIDE Felder ausgefüllt sind
            if (newPassword.trim() && confirmPassword.trim()) {
                hasChanges = true;
                const passwordSuccess = await handleChangePassword();
                if (!passwordSuccess) {
                    allSuccess = false;
                }
            } else if (newPassword.trim() || confirmPassword.trim()) {
                // Wenn nur eines der Felder ausgefüllt ist, ignoriere es (keine Fehlermeldung)
                // Das Passwort wird nicht geändert
            }

            // 4. Speichere Organisationsname falls geändert
            if (organizationName.trim() && organizationService) {
                const org = organizationService.getCurrentOrganization();
                if (org && org.name !== organizationName.trim()) {
                    hasChanges = true;
                    setIsSavingOrg(true);
                    try {
                        await organizationService.renameOrganization(org.id, organizationName.trim());
                    } catch (error) {
                        console.error('Fehler beim Ändern des Organisationsnamens:', error);
                        if (showNotification) {
                            showNotification(error.message || t('settings.organizationNameUpdateError', 'Fehler beim Ändern des Organisationsnamens'), 'error');
                        }
                        allSuccess = false;
                    } finally {
                        setIsSavingOrg(false);
                    }
                }
            }

            // 5. Sprache ist bereits in localStorage gespeichert (wird sofort gespeichert bei Änderung)

            if (!hasChanges) {
                if (showNotification) {
                    showNotification(t('settings.noChanges', 'Keine Änderungen zum Speichern'), 'info');
                }
                return;
            }

            if (allSuccess) {
                if (showNotification) {
                    showNotification(t('settings.saveAll', 'Alle Änderungen erfolgreich gespeichert'), 'success');
                }
                // Schließe Modal nach kurzer Verzögerung
                setTimeout(() => {
                    onClose();
                }, 500);
            } else {
                if (showNotification) {
                    showNotification(t('settings.partialSaveError', 'Einige Änderungen konnten nicht gespeichert werden'), 'error');
                }
            }
        } catch (error) {
            console.error('Fehler beim Speichern:', error);
            if (showNotification) {
                showNotification(t('settings.errorSaving', 'Fehler beim Speichern der Änderungen'), 'error');
            }
        } finally {
            setIsSavingName(false);
        }
    };

    if (!isOpen) return null;

    const isSaving = isSavingName || isSavingOrg || isUploadingImage || isChangingPassword;

    const TABS = [
        { id: 'personal', label: t('settings.personalSection', 'Profil'), icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/></svg> },
        { id: 'workspace', label: t('settings.workspaceSection', 'Workspace'), icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg> },
    ];

    return (
        <>
        <style>{`
            @keyframes stmIn { from { opacity: 0; transform: translateY(8px) scale(.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
            @keyframes stmFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
            .stm-input:focus { border-color: #818cf8 !important; box-shadow: 0 0 0 3px rgba(99,102,241,.1) !important; }
            .stm-select:focus { border-color: #818cf8 !important; box-shadow: 0 0 0 3px rgba(99,102,241,.1) !important; }
            .stm-tab:hover:not(.stm-tab--active) { background: #f8fafc; }
            .stm-member:hover { background: #f8fafc; }
            .stm-invite:hover { background: #f8fafc; }
            .stm-body::-webkit-scrollbar { width: 5px; }
            .stm-body::-webkit-scrollbar-track { background: transparent; }
            .stm-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }
            .stm-close:hover { background: #f1f5f9 !important; color: #334155 !important; }
            .stm-pw-toggle:hover { color: #4f46e5 !important; }
        `}</style>
        <div className="modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)', backdropFilter: 'blur(6px)' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, width: '95%', maxWidth: 720, height: '70vh', maxHeight: 620, display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,.18)', animation: 'stmIn 200ms ease-out', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/></svg>
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', letterSpacing: '-0.02em' }}>{t('settings.title', 'Einstellungen')}</span>
                    </div>
                    <button className="stm-close" onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 100ms ease' }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ padding: '12px 24px 0', display: 'flex', gap: 6, flexShrink: 0 }}>
                    {TABS.map(tab => {
                        const active = settingsActiveSection === tab.id;
                        return (
                            <button key={tab.id} type="button" className={`stm-tab ${active ? 'stm-tab--active' : ''}`} onClick={() => setSettingsActiveSection(tab.id)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: active ? 700 : 600, background: active ? '#eef2ff' : 'transparent', color: active ? '#4f46e5' : '#64748b', cursor: 'pointer', transition: 'all 120ms ease', display: 'flex', alignItems: 'center', gap: 7 }}>
                                <span style={{ color: active ? '#4f46e5' : '#94a3b8', transition: 'color 120ms ease' }}>{tab.icon}</span>
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="stm-body" style={{ flex: 1, minHeight: 320, overflowY: 'auto', padding: 24 }}>

                    {settingsActiveSection === 'personal' && (
                        <div style={{ animation: 'stmFade 150ms ease' }}>
                            {/* Profile + Name row */}
                            <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
                                {/* Avatar */}
                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ width: 72, height: 72, borderRadius: 16, overflow: 'hidden', border: '2px solid #e2e8f0', position: 'relative', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
                                        {profileImageUrl ? (
                                            <img src={profileImageUrl} alt="Profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ color: '#cbd5e1' }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/></svg>
                                        )}
                                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 150ms ease' }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="13" r="4" stroke="#fff" strokeWidth="2"/></svg>
                                        </div>
                                    </div>
                                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} style={{ display: 'none' }} />
                                </div>
                                {/* Name fields */}
                                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <SLabel>{t('settings.firstName', 'Vorname')}</SLabel>
                                        <SInput type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Max" disabled={isSavingName} />
                                    </div>
                                    <div>
                                        <SLabel>{t('settings.lastName', 'Nachname')}</SLabel>
                                        <SInput type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Mustermann" disabled={isSavingName} />
                                    </div>
                                </div>
                            </div>

                            {/* Password section */}
                            <div style={{ padding: 16, borderRadius: 14, background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: '#64748b' }}><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{t('settings.changePasswordTitle', 'Passwort ändern')}</span>
                                    </div>
                                    <button type="button" className="stm-pw-toggle" onClick={() => setShowPasswords(!showPasswords)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, transition: 'color 120ms ease' }}>
                                        <EyeIcon off={showPasswords} />
                                        {showPasswords ? t('settings.hidePassword', 'Verbergen') : t('settings.showPassword', 'Anzeigen')}
                                    </button>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <SLabel>{t('settings.newPassword', 'Neues Passwort')}</SLabel>
                                        <SInput type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" disabled={isChangingPassword || isSavingName} style={{ background: '#fff' }} />
                                    </div>
                                    <div>
                                        <SLabel>{t('settings.confirmPassword', 'Bestätigen')}</SLabel>
                                        <SInput type={showPasswords ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" disabled={isChangingPassword || isSavingName} style={{ background: '#fff' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {settingsActiveSection === 'workspace' && (
                        <div style={{ animation: 'stmFade 150ms ease' }}>
                            {/* Sidebar-Logo */}
                            {(currentOrgRole === 'admin' || currentOrgRole === 'owner') && (
                                <div style={{ marginBottom: 24 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: '#64748b' }}>
                                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                                            <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
                                            {t('settings.sidebarLogo.title', 'Sidebar-Logo')}
                                        </span>
                                    </div>
                                    <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                                        {t('settings.sidebarLogo.intro', 'Eigenes Logo für die Seitenleiste. Wird das Siqment-Logo ersetzen, solange ein Logo hinterlegt ist.')}
                                    </p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                        <div
                                            onClick={() => !isUploadingLogo && logoFileInputRef.current && logoFileInputRef.current.click()}
                                            style={{
                                                width: 88,
                                                height: 88,
                                                borderRadius: 12,
                                                border: '1.5px dashed #cbd5e1',
                                                background: '#f8fafc',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                cursor: isUploadingLogo ? 'not-allowed' : 'pointer',
                                                flexShrink: 0,
                                                position: 'relative'
                                            }}
                                            title={t('settings.sidebarLogo.chooseFile', 'Datei auswählen')}
                                        >
                                            {(pendingLogoPreviewUrl || organizationLogoUrl) ? (
                                                <img
                                                    src={pendingLogoPreviewUrl || organizationLogoUrl}
                                                    alt="Logo"
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ color: '#94a3b8' }}>
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </div>
                                        <input
                                            ref={logoFileInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                            onChange={handleLogoFileSelect}
                                            style={{ display: 'none' }}
                                        />
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                <SBtn
                                                    variant="secondary"
                                                    onClick={() => logoFileInputRef.current && logoFileInputRef.current.click()}
                                                    disabled={isUploadingLogo}
                                                >
                                                    {t('settings.sidebarLogo.chooseFile', 'Datei auswählen')}
                                                </SBtn>
                                                {pendingLogoFile && (
                                                    <SBtn variant="primary" onClick={handleLogoUpload} disabled={isUploadingLogo}>
                                                        {isUploadingLogo ? t('settings.sidebarLogo.uploading', 'Lade hoch...') : t('settings.sidebarLogo.upload', 'Speichern')}
                                                    </SBtn>
                                                )}
                                                {organizationLogoUrl && !pendingLogoFile && (
                                                    <SBtn variant="danger" onClick={handleLogoRemove} disabled={isUploadingLogo}>
                                                        {t('settings.sidebarLogo.remove', 'Entfernen')}
                                                    </SBtn>
                                                )}
                                            </div>
                                            <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                {t('settings.sidebarLogo.hint', 'PNG, JPG, SVG oder WEBP — max. 2 MB.')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Org + Language */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                                <div>
                                    <SLabel>{t('settings.organizationName', 'Organisation')}</SLabel>
                                    <SInput type="text" value={organizationName} onChange={e => setOrganizationName(e.target.value)} placeholder="Meine Firma GmbH" disabled={isSavingName || isSavingOrg} />
                                </div>
                                <div>
                                    <SLabel>{t('settings.language', 'Sprache')}</SLabel>
                                    <select className="stm-input stm-select" value={language} onChange={e => handleLanguageChange(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 500, color: '#1e293b', background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 150ms ease, box-shadow 150ms ease', cursor: 'pointer' }}>
                                        <option value="de">Deutsch</option>
                                        <option value="en">English</option>
                                    </select>
                                </div>
                            </div>

                            {/* Buchhaltung — Kontenrahmen (Phase 1) */}
                            <div style={{ marginBottom: 24 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: '#64748b' }}>
                                        <path d="M3 3h18v18H3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M3 9h18M9 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
                                        {t('settings.accounting.title', 'Buchhaltung')}
                                    </span>
                                </div>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                                    {t('settings.accounting.intro', 'Kontenrahmen dieser Organisation. Bestimmt das automatische Mapping beim Upload von DATEV-Kontenblättern.')}
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {[
                                        { value: 'SKR04', label: 'SKR04', description: t('settings.accounting.skr04Description', 'DATEV-Standardkontenrahmen für bilanz-orientierte Buchhaltung. Standardmapping aktiv.'), disabled: false, hint: null },
                                        { value: 'SKR03', label: 'SKR03', description: t('settings.accounting.skr03Description', 'DATEV-Standardkontenrahmen für prozess-orientierte Buchhaltung. Standardmapping aktiv.'), disabled: false, hint: null },
                                        { value: 'custom', label: t('settings.accounting.customLabel', 'Individuell'), description: t('settings.accounting.customDescription', 'Eigener Kontenrahmen — kommt bald.'), disabled: true, hint: null }
                                    ].map(opt => {
                                        const checked = chartOfAccounts === opt.value;
                                        return (
                                            <label
                                                key={opt.value}
                                                style={{
                                                    display: 'flex',
                                                    gap: 10,
                                                    padding: 12,
                                                    border: `1.5px solid ${checked ? '#818cf8' : '#e2e8f0'}`,
                                                    background: checked ? '#eef2ff' : '#fff',
                                                    borderRadius: 10,
                                                    cursor: opt.disabled ? 'not-allowed' : 'pointer',
                                                    opacity: opt.disabled ? 0.55 : 1,
                                                    transition: 'border-color 100ms ease, background 100ms ease'
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="stm-chart-of-accounts"
                                                    value={opt.value}
                                                    checked={checked}
                                                    disabled={opt.disabled || isSavingChartOfAccounts}
                                                    onChange={() => handleChartOfAccountsChange(opt.value)}
                                                    style={{ marginTop: 3, cursor: opt.disabled ? 'not-allowed' : 'pointer' }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{opt.label}</div>
                                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{opt.description}</div>
                                                    {opt.hint && (
                                                        <div style={{ marginTop: 6, padding: '6px 8px', background: '#fff7ed', color: '#9a3412', fontSize: 11, borderRadius: 6, lineHeight: 1.45 }}>
                                                            ⏳ {opt.hint}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Team Members */}
                            {canManageMembers && (
                                <div style={{ marginBottom: 20 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: '#64748b' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{t('settings.members.title', 'Team-Mitglieder')}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>({members.length})</span>
                                        </div>
                                        <SBtn variant="sm" onClick={() => loadMembers()} disabled={isLoadingMembers}>
                                            {isLoadingMembers ? '...' : t('settings.members.refresh', 'Aktualisieren')}
                                        </SBtn>
                                    </div>
                                    <div style={{ borderRadius: 12, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                                        {isLoadingMembers ? (
                                            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('settings.members.loading', 'Lade...')}</div>
                                        ) : members.length === 0 ? (
                                            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('settings.members.none', 'Keine Mitglieder')}</div>
                                        ) : members.map((member) => {
                                            const memberRole = String(member.role || 'member').toLowerCase();
                                            const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
                                            const identity = member.email || member.user_id;
                                            const isOwner = memberRole === 'owner';
                                            const isUpdatingMember = updatingMemberRoleUserId === member.user_id;
                                            const isRemovingMember = removingMemberUserId === member.user_id;
                                            const isTransferringOwner = transferringOwnerUserId === member.user_id;
                                            const canRemoveMember = !member.is_self && !isOwner;
                                            const canTransferOwnerHere = currentOrgRole === 'owner' && !member.is_self && !isOwner;
                                            return (
                                                <div key={`${member.user_id}-${member.role}`} className="stm-member" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f1f5f9', transition: 'background 80ms ease' }}>
                                                    <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#94a3b8', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                                                        {(memberName || identity).charAt(0)}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {memberName || identity}
                                                            {member.is_self && <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', marginLeft: 4 }}>({t('settings.members.me', 'ich')})</span>}
                                                        </div>
                                                        {memberName && <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</div>}
                                                    </div>
                                                    {isOwner ? (
                                                        <RoleBadge role="owner" />
                                                    ) : (
                                                        <select className="stm-input stm-select" value={memberRole} onChange={e => handleMemberRoleChange(member, e.target.value)} disabled={isUpdatingMember || isRemovingMember} style={{ width: 'auto', padding: '4px 8px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#475569', background: '#fff', outline: 'none', cursor: 'pointer' }}>
                                                            <option value="viewer">viewer</option>
                                                            <option value="member">member</option>
                                                            <option value="editor">editor</option>
                                                            <option value="admin">admin</option>
                                                        </select>
                                                    )}
                                                    {canTransferOwnerHere && (
                                                        <SBtn variant="sm" onClick={() => handleTransferOwner(member)} disabled={isUpdatingMember || isRemovingMember || isTransferringOwner} title={t('settings.members.transferOwnership', 'Inhaberschaft übertragen')}>
                                                            {isTransferringOwner ? '...' : 'Owner'}
                                                        </SBtn>
                                                    )}
                                                    {canRemoveMember && (
                                                        <SBtn variant="danger" onClick={() => handleRemoveMember(member)} disabled={isUpdatingMember || isRemovingMember || isTransferringOwner} style={{ height: 30, padding: '0 8px' }}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                        </SBtn>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Invites */}
                            {canManageInvites && (
                                <div>
                                    <button type="button" onClick={toggleInvitesSection} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 0, border: 'none', background: 'none', cursor: 'pointer', marginBottom: invitesSectionExpanded ? 12 : 0 }}>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: invitesSectionExpanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 150ms ease', color: '#94a3b8' }}><path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{t('settings.invites.title', 'Einladungen')}</span>
                                        {invites.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>({invites.length})</span>}
                                    </button>

                                    {invitesSectionExpanded && (
                                        <div style={{ animation: 'stmFade 150ms ease' }}>
                                            {/* Create invite */}
                                            <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #f1f5f9', marginBottom: 12 }}>
                                                <SLabel>{t('settings.invites.new', 'Neue Einladung')}</SLabel>
                                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                    <SInput type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="name@firma.de" disabled={isCreatingInvite} style={{ flex: '1 1 160px', background: '#fff' }} />
                                                    <select className="stm-input stm-select" value={inviteRole} onChange={e => setInviteRole(e.target.value)} disabled={isCreatingInvite} style={{ width: 'auto', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 500, color: '#1e293b', background: '#fff', outline: 'none', cursor: 'pointer' }}>
                                                        <option value="viewer">Viewer</option>
                                                        <option value="editor">Editor</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                    <select className="stm-input stm-select" value={inviteExpiresInHours} onChange={e => setInviteExpiresInHours(Number(e.target.value))} disabled={isCreatingInvite} style={{ width: 'auto', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 500, color: '#1e293b', background: '#fff', outline: 'none', cursor: 'pointer' }}>
                                                        <option value={24}>24h</option>
                                                        <option value={72}>72h</option>
                                                        <option value={168}>7 Tage</option>
                                                    </select>
                                                    <SBtn variant="primary" onClick={handleCreateInvite} disabled={isCreatingInvite || !inviteEmail.trim()} style={{ flexShrink: 0 }}>
                                                        {isCreatingInvite ? '...' : t('settings.invites.invite', 'Einladen')}
                                                    </SBtn>
                                                </div>
                                            </div>

                                            {lastInviteToken && (
                                                <div style={{ padding: 12, borderRadius: 10, background: '#ecfdf3', border: '1px solid #a7f3d0', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <SInput type="text" value={getInviteLinkFromToken(lastInviteToken)} readOnly style={{ flex: 1, fontSize: 11, padding: '6px 10px', background: '#fff' }} />
                                                    <SBtn variant="sm" onClick={handleCopyInviteLink}>
                                                        {inviteLinkCopied ? t('settings.invites.copied', 'Kopiert!') : t('settings.invites.copy', 'Kopieren')}
                                                    </SBtn>
                                                </div>
                                            )}

                                            {/* Invite list */}
                                            <div style={{ borderRadius: 12, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                                                {isLoadingInvites ? (
                                                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('settings.invites.loading', 'Lade...')}</div>
                                                ) : invites.length === 0 ? (
                                                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('settings.invites.none', 'Keine Einladungen')}</div>
                                                ) : invites.map(invite => {
                                                    const isPending = String(invite.status || '').toLowerCase() === 'pending';
                                                    const statusLabel = getInviteStatusLabel(invite);
                                                    const isDeleting = deletingInviteId === invite.id;
                                                    return (
                                                        <div key={invite.id || `${invite.email}-${invite.created_at}`} className="stm-invite" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f1f5f9', transition: 'background 80ms ease', opacity: isDeleting ? 0.4 : 1 }}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{invite.email}</div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                                                                    <RoleBadge role={invite.role} />
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: '#64748b' }}>
                                                                        <StatusDot status={(invite.status || '').toLowerCase()} />
                                                                        {statusLabel}
                                                                    </span>
                                                                    <span style={{ fontSize: 10, color: '#cbd5e1' }}>{formatDate(invite.expires_at)}</span>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                                                {storedInviteLinks[invite.id] && (
                                                                    <SBtn variant="sm" onClick={() => handleCopyInviteLinkForId(invite.id)}>
                                                                        {inviteLinkCopiedId === invite.id ? 'Kopiert!' : 'Link'}
                                                                    </SBtn>
                                                                )}
                                                                {isPending && (
                                                                    <SBtn variant="danger" onClick={() => handleRevokeInvite(invite.id)} disabled={revokingInviteId === invite.id || isDeleting} style={{ height: 30 }}>
                                                                        {revokingInviteId === invite.id ? '...' : t('settings.invites.revoke', 'Zurückziehen')}
                                                                    </SBtn>
                                                                )}
                                                                <SBtn variant="ghost" onClick={() => handleRequestInviteDelete(invite)} disabled={isDeleting} style={{ color: '#ef4444' }}>
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                                </SBtn>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '14px 24px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
                    <SBtn variant="secondary" onClick={onClose}>
                        {t('common.cancel', 'Abbrechen')}
                    </SBtn>
                    <SBtn variant="primary" onClick={handleSaveAll} disabled={isSaving}>
                        {isSaving ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: 'spin 800ms linear infinite' }}><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="2" strokeDasharray="20 12" strokeLinecap="round"/></svg>
                                {t('settings.saving', 'Speichern…')}
                            </>
                        ) : (
                            <>
                                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2.5 7l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                {t('common.save', 'Speichern')}
                            </>
                        )}
                    </SBtn>
                </div>
            </div>
        </div>
        {pendingInviteDelete && (
            <DeleteConfirmModal
                isOpen={true}
                onClose={handleCloseInviteDeleteModal}
                onConfirm={handleConfirmInviteDelete}
                title={t('settings.invites.delete', 'Einladung löschen')}
                message={t('settings.invites.deleteConfirm', 'Möchtest du die Einladung für "{email}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.', { email: pendingInviteDelete.email || t('settings.invites.thisUser', 'diesen Nutzer') })}
            />
        )}
        </>
    );
};

// Global export
export default SettingsModal;
